import { useState, useEffect, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { MapPin, Filter, X } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const DEFAULT_PHOTO = "https://images.pexels.com/photos/30892994/pexels-photo-30892994.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=400&w=400";

const resolvePhoto = (url) => {
  if (!url) return DEFAULT_PHOTO;
  return url.startsWith("/") ? `${API}${url}` : url;
};

const UK_REGIONS = [
  "London", "South East", "South West", "East of England", "East Midlands",
  "West Midlands", "Yorkshire and The Humber", "North West", "North East",
  "Scotland", "Wales", "Northern Ireland"
];
const CUISINES = [
  "British", "French", "Italian", "Spanish", "Greek", "Indian", "Japanese", "Chinese",
  "Thai", "Asian Fusion", "Mediterranean", "Modern European", "Middle Eastern",
  "Mexican", "American", "Vegetarian", "Vegan", "Seafood"
];

export default function SearchPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [chefs, setChefs] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);

  const [region, setRegion] = useState(searchParams.get("region") || "");
  const [cuisine, setCuisine] = useState(searchParams.get("cuisine") || "");
  const [date, setDate] = useState(searchParams.get("date") || "");

  const fetchChefs = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (region) params.set("region", region);
      if (cuisine) params.set("cuisine", cuisine);
      if (date) params.set("date", date);
      const { data } = await axios.get(`${API}/api/chefs?${params.toString()}`);
      setChefs(data.chefs || []);
      setTotal(data.total || 0);
    } catch {
      setChefs([]);
    } finally {
      setLoading(false);
    }
  }, [region, cuisine, date]);

  useEffect(() => { fetchChefs(); }, [fetchChefs]);

  const applyFilters = () => {
    const params = new URLSearchParams();
    if (region) params.set("region", region);
    if (cuisine) params.set("cuisine", cuisine);
    if (date) params.set("date", date);
    setSearchParams(params);
    setShowFilters(false);
    fetchChefs();
  };

  const clearFilters = () => {
    setRegion(""); setCuisine(""); setDate("");
    setSearchParams({});
  };

  const hasFilters = region || cuisine || date;

  return (
    <div className="min-h-screen bg-[#050505] pt-16">
      {/* Header */}
      <div className="border-b border-[#D4AF37]/15 bg-[#080808] py-10">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <p className="text-xs tracking-[0.35em] uppercase text-[#D4AF37] mb-2">Directory</p>
          <h1 className="font-serif text-4xl sm:text-5xl text-white font-light mb-4">Find a Private Chef</h1>
          <div className="flex items-center gap-4 flex-wrap">
            <span className="text-[#A0A0A0] text-sm">{loading ? "Searching..." : `${total} chef${total !== 1 ? "s" : ""} found`}</span>
            {hasFilters && (
              <div className="flex items-center gap-2 flex-wrap">
                {region && <FilterTag label={region} onRemove={() => { setRegion(""); }} />}
                {cuisine && <FilterTag label={cuisine} onRemove={() => { setCuisine(""); }} />}
                {date && <FilterTag label={date} onRemove={() => { setDate(""); }} />}
                <button onClick={clearFilters} className="text-xs text-[#A0A0A0] hover:text-white underline ml-1">Clear all</button>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-8">
        {/* Filter Bar */}
        <div className="mb-8 bg-[#0A0A0A] border border-[#D4AF37]/15 p-4 sm:p-6">
          <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 items-end">
            <div>
              <label className="text-xs tracking-[0.2em] uppercase text-[#D4AF37] block mb-2">Region</label>
              <select
                data-testid="filter-region-select"
                value={region}
                onChange={e => setRegion(e.target.value)}
                className="w-full bg-transparent border-b border-white/20 focus:border-[#D4AF37] py-2 text-white text-sm outline-none transition-colors cursor-pointer"
              >
                <option value="" className="bg-[#121212]">All Regions</option>
                {UK_REGIONS.map(r => <option key={r} value={r} className="bg-[#121212]">{r}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs tracking-[0.2em] uppercase text-[#D4AF37] block mb-2">Cuisine</label>
              <select
                data-testid="filter-cuisine-select"
                value={cuisine}
                onChange={e => setCuisine(e.target.value)}
                className="w-full bg-transparent border-b border-white/20 focus:border-[#D4AF37] py-2 text-white text-sm outline-none transition-colors cursor-pointer"
              >
                <option value="" className="bg-[#121212]">All Cuisines</option>
                {CUISINES.map(c => <option key={c} value={c} className="bg-[#121212]">{c}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs tracking-[0.2em] uppercase text-[#D4AF37] block mb-2">Available Date</label>
              <input
                data-testid="filter-date-input"
                type="date"
                value={date}
                onChange={e => setDate(e.target.value)}
                min={new Date().toISOString().split("T")[0]}
                className="w-full bg-transparent border-b border-white/20 focus:border-[#D4AF37] py-2 text-white text-sm outline-none transition-colors"
              />
            </div>
            <button
              data-testid="filter-apply-btn"
              onClick={applyFilters}
              className="bg-[#D4AF37] text-black hover:bg-[#F3E5AB] py-2.5 px-6 text-sm font-medium tracking-wide transition-colors duration-200"
            >
              Apply Filters
            </button>
          </div>
        </div>

        {/* Results Grid */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-[#D4AF37]/10">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="bg-[#0A0A0A] p-6 animate-pulse">
                <div className="h-48 bg-[#1A1A1A] mb-4" />
                <div className="h-4 bg-[#1A1A1A] mb-2 w-3/4" />
                <div className="h-3 bg-[#1A1A1A] w-1/2" />
              </div>
            ))}
          </div>
        ) : chefs.length === 0 ? (
          <div className="text-center py-24 border border-[#D4AF37]/10">
            <p className="font-serif text-3xl text-[#D4AF37] mb-3">No chefs found</p>
            <p className="text-[#A0A0A0] mb-6">Try adjusting your filters or browse all chefs.</p>
            <button onClick={clearFilters}
              className="border border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37] hover:text-black px-8 py-3 text-sm tracking-wide transition-all duration-200">
              Clear Filters
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-[#D4AF37]/10">
            {chefs.map(chef => (
              <ChefCard key={chef._id} chef={chef} onClick={() => navigate(`/chef/${chef._id}`)} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function FilterTag({ label, onRemove }) {
  return (
    <span className="flex items-center gap-1 text-xs px-3 py-1 border border-[#D4AF37]/30 text-[#D4AF37]">
      {label}
      <button onClick={onRemove} className="hover:text-white ml-1"><X size={10} /></button>
    </span>
  );
}

function ChefCard({ chef, onClick }) {
  return (
    <div
      data-testid={`chef-card-${chef._id}`}
      onClick={onClick}
      className="bg-[#0A0A0A] hover:bg-[#111] group cursor-pointer transition-all duration-300 hover:-translate-y-0.5 hover:shadow-[0_8px_32px_rgba(212,175,55,0.08)]"
    >
      <div className="h-52 overflow-hidden relative">
        <img
          src={resolvePhoto(chef.photo_url)}
          alt={chef.chef_name}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
        {chef.availability_dates?.length > 0 && (
          <div className="absolute top-3 right-3 bg-[#D4AF37] text-black text-xs px-2 py-0.5 font-medium">
            Available
          </div>
        )}
      </div>
      <div className="p-6">
        <div className="flex items-center gap-1.5 mb-2">
          <MapPin size={11} className="text-[#D4AF37]" />
          <span className="text-xs tracking-[0.15em] uppercase text-[#D4AF37]">
            {chef.regions?.[0] || "UK"}
          </span>
        </div>
        <h3 className="font-serif text-xl text-white mb-2 group-hover:text-[#D4AF37] transition-colors">
          {chef.chef_name}
        </h3>
        {chef.bio && (
          <p className="text-[#A0A0A0] text-sm mb-4 line-clamp-2">{chef.bio}</p>
        )}
        <div className="flex flex-wrap gap-1.5 mb-4">
          {(chef.cuisine_specialties || []).slice(0, 3).map(c => (
            <span key={c} className="text-xs px-2 py-0.5 border border-[#D4AF37]/20 text-[#A0A0A0]">{c}</span>
          ))}
        </div>
        <div className="flex items-center justify-between pt-4 border-t border-white/5">
          {chef.price_range_min ? (
            <span className="text-[#A0A0A0] text-sm">From £{chef.price_range_min}</span>
          ) : (
            <span className="text-[#A0A0A0] text-sm">Rate on request</span>
          )}
          <span className="text-[#D4AF37] text-sm">View →</span>
        </div>
      </div>
    </div>
  );
}
